import axios from 'axios';
import { unescape } from 'html-escaper';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import values from 'lodash/values';
import { AnvilForm } from 'components/anvilFormFillable/fields/types';
import { NormalizeFormTemplateMetaDataResponse } from 'types/normalizedFormTemplate';
import dayjs, { Dayjs } from 'dayjs';
import {
  getOrgNow,
} from 'utils/dayjs';
import isObject from 'lodash/isObject';
import {
  AddressDTO,
  PayerEntryAddressDTO,
  FieldValueType,
  FormsRenderingMetadataResponseDTO,
  InteractiveFormFieldValueDTO,
  EMRTypes,
} from 'artifacts/dtos';
import { isTabletBaseIntake } from 'env';
import { FormFieldsData } from 'components/anvilFormFillable';
import toPairs from 'lodash/toPairs';
import flatMap from 'lodash/flatMap';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import filter from 'lodash/filter';
import { getFieldTypeByField } from 'components/anvilFormFillable/utils';

type ComplexHeight = {
  feet?: number;
  inches?: number;
}

export function isEmptyString(value: any) {
  return isNil(value) || (typeof value === 'string' && value.trim() === '');
}

// Valid:
// John
// José
// Muñoz
// François
// Müller
// محمد
// 李
// Иван
// Mary Ann
// Jean-Luc
// O'Brien
// O’Brien
// John2
// 3M
// 123

// Invalid:
// empty
// John,
// John@Doe
// -Smith
// Mary  Ann
// O''Brien
// O‘Brien
export const PATIENT_NAME_REGEX = /^[\p{L}\p{M}\p{N}]+(?:[ '\u2019-][\p{L}\p{M}\p{N}]+)*$/u;

export function normalizePatientName(value?: string | null): string {
  return value?.trim() ?? '';
}

export function validatePatientName(
  value: string | null | undefined,
  message: string,
  required: boolean = true,
): true | string {
  const trimmed = normalizePatientName(value);
  if (isEmptyString(trimmed)) {
    return required ? message : true;
  }
  return PATIENT_NAME_REGEX.test(trimmed) ? true : message;
}

export const filterIntakeFormSettings = (data: any, key: string) => data[key] ?? [];

export const clearLocalStorage = async () => {
  localStorage.clear();
};

export const removeItemFromLocalStorage = async (key: string) => {
  localStorage.removeItem(key);
};

const EMAIL_REGEX = /^(?:[A-Za-z0-9](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*[A-Za-z0-9])?)*|"[^"]+")@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
const PHONE_REGEX = /^(\+1)?(\d{3})(\d{3})(\d{4})$/i;

export const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email);
export const validateDate = (
  date: string,
): boolean => !isEmptyString(date) && dayjs(date).isValid();

// @TODO: rename to zipCode validation
export const numberValidation = (value: string) => /^\d{5}$/.test(value);

export const isValidWeight = (value: number): boolean => value > 0 && value <= 600;

export const stringValidation = (value: string) => {
  const regex = /^[a-zA-Z ]*$/; // string and space
  return regex.test(value);
};

export const parseErrorMessage = (error: unknown, defaultMsg = 'Something went wrong.'): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && !isEmptyString(data)) {
      return data;
    }
    if (isObject(data)) {
      const {
        errors,
        detail,
        message,
        title,
      } = data as Record<string, any>;
      if (errors && isObject(errors)) {
        const list = values(errors).flat().filter(Boolean);
        if (list.length > 0) return list.map(String).join(' ');
      }
      const text = detail ?? message ?? title;
      if (typeof text === 'string' && !isEmptyString(text)) return text;
    }
    if (typeof error.message === 'string' && !isEmptyString(error.message)) return error.message;
  }
  if (error instanceof Error && typeof error.message === 'string' && !isEmptyString(error.message)) {
    return error.message;
  }
  return typeof error === 'string' && !isEmptyString(error) ? error : defaultMsg;
};

export async function blobToBase64(blob: Blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

export const unescapeHtml = (content: string, currentDate: string) => unescape(content).replaceAll('{Date}', `<b>${currentDate}</b>`);

// eslint-disable-next-line no-useless-escape
export const restrictComma = (value: string) => /^[^\,]*$/.test(value);

export const em = (em: number = 1, relativeSize: number = 16) => em * relativeSize;

export function getFullName({ firstName = '', lastName = '', middleName = '' }): string {
  return compact([firstName, middleName, lastName]).join(' ');
}

export function getAddressObject(address?: AddressDTO) {
  const {
    street = '',
    unit = '',
    city = '',
    state = '',
    zipCode = '',
    shortZipCode,
  } = address ?? {};
  const streetUnit = compact([street, unit]).join(', ');
  const cityStateZip = compact([
    city,
    compact([state, shortZipCode ?? zipCode]).join(' '),
  ]).join(', ');
  return { streetUnit, cityStateZip };
}

export function getAddressString(address?: AddressDTO | PayerEntryAddressDTO, defaultValue = '--') {
  const { streetUnit, cityStateZip } = getAddressObject(address as AddressDTO);
  return !isNil(address) && !values(address).every(isEmptyString)
    ? compact([streetUnit, cityStateZip]).join(', ')
    : defaultValue;
}

export function formatPhone(phone: string) {
  const [match, , areaCode, start, end] = phone.match(PHONE_REGEX) ?? [];
  return isNil(match) ? '--' : `(${areaCode}) ${start}-${end}`;
}

export function formatDate(date: Date | string) {
  return dayjs(date).format('M/D/YYYY');
}

export function formatSignatureTimestamp(date: Dayjs = getOrgNow()) {
  if (!date.isValid()) {
    return '';
  }

  const timezoneAbbreviation = date.offsetName();

  return timezoneAbbreviation
    ? `${date.format('M/D/YYYY h:mm A')} ${timezoneAbbreviation}`
    : date.format('M/D/YYYY h:mm A');
}

export function formatDateOnly(date: Dayjs | string) {
  return dayjs(date).format('YYYY-MM-DD');
}
export function getUniqueFormName(str: string, uniqueNumberId: string) {
  return `${str.replace(/[^A-Z0-9]+/ig, '-')}-${uniqueNumberId}`;
}
export function getBytesToMegabytes(bytes: number = 0) {
  return bytes / 1024 / 1024;
}
export const phoneValidation = (phone: string): boolean => /^\d{11}$/i.test(phone);

export const clearAppLocalStorage = () => localStorage.clear();

export function getFileExtension(source: string = ''): string | null {
  if (source.startsWith('data:')) {
    const mimeType = source.split(';')[0].split(':')[1] ?? '';
    const ext = mimeType.split('/')[1] ?? '';
    return ext ? ext.toLowerCase() : null;
  }

  const [_, ext] = source.match(/^.*\.(\w+)$/) ?? [null, null];
  return ext ? ext.toLowerCase() : null;
}
export function getUserInitials(fullName: string = ''): string | null {
  const parts = fullName.split(' ');
  const result = parts.length >= 3
    ? [parts[0][0], parts[2][0]].join('')
    : compact(parts).map((part) => part[0]).join('');

  return result === '' ? null : result.toUpperCase();
}

export const parseHeightInFeet = (heightInInches: number): ComplexHeight => {
  const feet = Math.floor(heightInInches / 12);
  const inches = Math.floor(heightInInches % 12);
  return { feet, inches };
};

export function pluralize(
  word: string,
  count: number = 0,
  showCount: boolean = true,
) {
  return `${showCount ? `${count} ` : ''}${word}${count === 1 ? '' : 's'}`;
}

const placeholderRegex = /{([^}]+)}/g;

export function replacePlaceholders(
  template: string,
  replacements: Record<string, string>,
): string {
  const replacePlaceholder = (
    match: string,
    placeholder: string,
  ) => replacements[placeholder] ?? match;
  const updatedTemplate: string = template.replace(placeholderRegex, replacePlaceholder);
  return updatedTemplate;
}

export function getLinkQueryParams(query: Record<string, any>) {
  const { token, t, s } = query;
  const emrType = s?.toLowerCase() === 'm' ? EMRTypes.ModMed : EMRTypes.Populate;

  return {
    token: t ?? token,
    emrType,
  };
}

export function getAccessInfo(query: Record<string, any>) {
  const { token, emrType } = getLinkQueryParams(query);

  return {
    token,
    emrType,
    canAccessPatientPortal: !isTabletBaseIntake && emrType !== EMRTypes.ModMed,
  };
}

export function normalizeInteractiveFieldValues(data: FormFieldsData)
: InteractiveFormFieldValueDTO[] {
  return flatMap(
    filter(toPairs(data), ([key, value]) => isObject(value) && 'type' in value && key.includes(':')),
    ([, field]) => {
      const typedField = field as AnvilForm;
      const formId = typedField.formId ?? 0;

      if (typedField?.type === 'checkboxGroup' && isArray(typedField.value)) {
        return typedField.value.map((childId) => ({
          formId,
          fieldId: childId.toString(),
          value: '✔',
          type: FieldValueType.Text,
        }));
      }

      if (typedField?.type === 'radioGroup' && isArray(typedField.children) && typedField.value) {
        const match = find(typedField.children, { id: typedField.value as string });
        return [{
          formId,
          fieldId: match?.id?.toString() ?? '',
          value: '✔',
          type: FieldValueType.Text,
        }];
      }

      if (typedField?.type === 'checkbox') {
        return [{
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: typedField.value === true ? '✔' : '',
          type: FieldValueType.Text,
        }];
      }
      if (typedField?.type === 'dollar' && !isEmptyString(typedField?.value)) {
        return [{
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: `$${typedField?.value}`,
          type: FieldValueType.Text,
        }];
      }
      if (typedField?.type === 'percent' && !isEmptyString(typedField?.value)) {
        return [{
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: `${typedField?.value}%`,
          type: FieldValueType.Text,
        }];
      }

      const rawValue = typedField?.value?.toString() ?? '';
      const isSignatureField = typedField?.type === 'signature' || typedField?.type === 'initial';
      const isImage = isSignatureField && /^data:image\//i.test(rawValue);

      return [{
        formId,
        fieldId: typedField.id?.toString() ?? '',
        value: rawValue,
        type: isImage ? FieldValueType?.ImageDataURI : FieldValueType?.Text,
      }];
    },
  );
}

export function serializeFormTemplateMetaData(
  data: FormsRenderingMetadataResponseDTO,
): NormalizeFormTemplateMetaDataResponse {
  return {
    ...data,
    formTemplates: (data.renderingData ?? []).map((template) => ({
      ...template,
      value: '',
      fields: template.fields?.map((field) => ({
        ...field,
        type: getFieldTypeByField(field as AnvilForm),
      })) ?? [],
    })),
  };
}

export function formatWithAffix(
  value: string | number,
  options?: { prefix?: string; suffix?: string },
): string {
  const str = String(value ?? '');
  const { prefix = '', suffix = '' } = options ?? {};
  return isEmptyString(value) ? '' : `${prefix}${str}${suffix}`;
}

// Flexible date parsing with two-digit year auto-correction (ported from PatientPortalApp)
const allowedFormats = [
  'MM/DD/YYYY',
  'MM/DD/YY',
];

export function parseFlexibleDate(input: string): Dayjs | null {
  let parsedDate: Dayjs | null = null;

  if (!isEmptyString(input) && typeof input === 'string') {
    const cleanInput = input.trim();

    // Check if input matches MM/DD/YY format specifically
    const twoDigitYearMatch = cleanInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/) ?? [];

    const month = parseInt((twoDigitYearMatch as any)[1], 10);
    const day = parseInt((twoDigitYearMatch as any)[2], 10);
    const twoDigitYear = parseInt((twoDigitYearMatch as any)[3], 10);
    const isValid = month > 0 && month <= 12 && day > 0 && day <= 31;

    if (twoDigitYearMatch && isValid) {
      const currentYear = dayjs().year();
      const currentTwoDigit = currentYear % 100;

      const decade = twoDigitYear <= currentTwoDigit ? 2000 : 1900;
      const fullYear = decade + twoDigitYear;

      const tentativeDate = dayjs(`${month}/${day}/${fullYear}`, 'M/D/YYYY', true);
      const isValidDate = tentativeDate.isValid()
        && tentativeDate.month() === month - 1
        && tentativeDate.date() === day
        && tentativeDate.year() === fullYear;

      parsedDate = isValidDate ? tentativeDate : null;
    } else {
      // Only allow complete date formats, reject incomplete dates
      const validFormat = allowedFormats.find((format) => {
        const parsed = dayjs(cleanInput, format, true);
        return parsed.isValid();
      });

      // Only parse if it matches one of our allowed complete formats
      if (!isNil(validFormat)) {
        // Additional validation: ensure input has the right number of parts
        const separator = validFormat.includes('/') ? '/' : '-';
        const expectedParts = validFormat.split(separator).length;
        const actualParts = cleanInput.split(separator).length;

        // Also check that the input uses the same separator as the format
        let inputSeparator = '';
        if (cleanInput.includes('/')) {
          inputSeparator = '/';
        } else if (cleanInput.includes('-')) {
          inputSeparator = '-';
        }

        if (expectedParts === actualParts && separator === inputSeparator) {
          const parsed = dayjs(cleanInput, validFormat, true);
          parsedDate = parsed.isValid() ? parsed : null;
        }
      }
    }
  }

  return parsedDate;
}

export function getPCCPatientId(accessToken?: string | null): number {
  try {
    const payload = accessToken?.split('.')[1];
    if (!payload) throw new Error();
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
    const patientId = Number(claims.PatientId);
    if (!Number.isSafeInteger(patientId) || patientId <= 0) throw new Error();
    return patientId;
  } catch {
    throw new Error('Unable to identify the patient. Please request a new PCC link.');
  }
}

export type CalendarBadgeDateInfo = {
  ccmMonth: string;
  ccmDay: string;
  formattedCcmFullDate: string;
};

export function formatCcmCallDate(
  rawDate?: string | Dayjs | Date | null,
): CalendarBadgeDateInfo {
  const dateObj = dayjs(rawDate);
  const isValid = !isNil(rawDate) && dateObj.isValid();

  return {
    ccmMonth: isValid ? dateObj.format('MMM').toUpperCase() : '',
    ccmDay: isValid ? dateObj.format('D') : '',
    formattedCcmFullDate: isValid ? dateObj.format('ddd, MMM D, YYYY') : '',
  };
}
